import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { withTranslation } from "react-i18next";
import { FiUser, FiMail, FiPhone, FiMessageSquare, FiSend } from "react-icons/fi";
import { toast } from "react-toastify";
import ProfileService from "../../api/ProfileService";
import { REACT_APP_SITE_KEY } from "../../config/app-config";

const inputBase = {
  width: "100%",
  border: "1.5px solid #e0e0e0",
  borderRadius: 8,
  padding: "10px 12px 10px 38px",
  fontSize: 13,
  color: "#1a1a1a",
  outline: "none",
  background: "#fafafa",
  fontFamily: "inherit",
  transition: "border 0.15s, background 0.15s",
};

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: "#555",
  marginBottom: 5,
  textTransform: "uppercase",
  letterSpacing: "0.6px",
};

const iconWrap = {
  position: "relative",
};

const iconStyle = {
  position: "absolute",
  left: 12,
  top: "50%",
  transform: "translateY(-50%)",
  color: "#bbb",
  pointerEvents: "none",
};

const ContactSupportForm = ({ t, versionV2, closeModel }) => {
  const recaptcha = useRef();
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    enquiry: "",
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const tempErrors = {};
    if (!formData.username || formData.username.length < 2 || formData.username.length > 50)
      tempErrors.username = "Name must be between 2 and 50 characters";
    if (!formData.email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(formData.email))
      tempErrors.email = "Please enter a valid email address";
    if (!formData.phone)
      tempErrors.phone = "Phone number is required";
    if (!formData.enquiry || formData.enquiry.length < 10 || formData.enquiry.length > 800)
      tempErrors.enquiry = "Enquiry must be between 10 and 800 characters";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const captchaValue = recaptcha?.current?.getValue();
    if (!captchaValue) {
      toast.warning("Please verify the reCAPTCHA!");
      return;
    }
    if (!validateForm()) {
      toast.warning("Please fill all required fields correctly");
      return;
    }
    setLoading(true);
    try {
      await ProfileService.saveEnquiry({
        name: formData.username,
        emailId: formData.email,
        phoneNumber: formData.phone,
        description: formData.enquiry,
      });
      recaptcha?.current?.reset();
      setFormData({ username: "", email: "", phone: "", enquiry: "" });
      setErrors({});
      toast.success("Thank you! We'll get back to you within 2 hours.");
      if (versionV2) closeModel();
    } catch {
      recaptcha?.current?.reset();
      toast.error("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const getBorderColor = (field) =>
    errors[field] ? "#e53935" : focusedField === field ? "#7a0024" : "#e0e0e0";

  const getBg = (field) =>
    focusedField === field ? "#fff" : "#fafafa";

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* Two-column row: Name + Phone */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        {/* Name */}
        <div>
          <label style={labelStyle}>{t("Full Name")} <span style={{ color: "#7a0024" }}>*</span></label>
          <div style={iconWrap}>
            <FiUser size={14} style={{ ...iconStyle }} />
            <input
              name="username"
              type="text"
              placeholder="Jane Smith"
              value={formData.username}
              onChange={handleChange}
              onFocus={() => setFocusedField("username")}
              onBlur={() => setFocusedField(null)}
              style={{ ...inputBase, borderColor: getBorderColor("username"), background: getBg("username") }}
            />
          </div>
          {errors.username && <div style={{ fontSize: 11, color: "#e53935", marginTop: 4 }}>{errors.username}</div>}
        </div>

        {/* Phone */}
        <div>
          <label style={labelStyle}>{t("Phone Number")} <span style={{ color: "#7a0024" }}>*</span></label>
          <div style={iconWrap}>
            <FiPhone size={14} style={{ ...iconStyle }} />
            <input
              name="phone"
              type="tel"
              placeholder="+91 98765 43210"
              value={formData.phone}
              onChange={handleChange}
              onFocus={() => setFocusedField("phone")}
              onBlur={() => setFocusedField(null)}
              style={{ ...inputBase, borderColor: getBorderColor("phone"), background: getBg("phone") }}
            />
          </div>
          {errors.phone && <div style={{ fontSize: 11, color: "#e53935", marginTop: 4 }}>{errors.phone}</div>}
        </div>
      </div>

      {/* Email */}
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>{t("Email Address")} <span style={{ color: "#7a0024" }}>*</span></label>
        <div style={iconWrap}>
          <FiMail size={14} style={{ ...iconStyle }} />
          <input
            name="email"
            type="email"
            placeholder="jane@company.com"
            value={formData.email}
            onChange={handleChange}
            onFocus={() => setFocusedField("email")}
            onBlur={() => setFocusedField(null)}
            style={{ ...inputBase, borderColor: getBorderColor("email"), background: getBg("email") }}
          />
        </div>
        {errors.email && <div style={{ fontSize: 11, color: "#e53935", marginTop: 4 }}>{errors.email}</div>}
      </div>

      {/* Enquiry */}
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>{t("How can we assist?")} <span style={{ color: "#7a0024" }}>*</span></label>
        <div style={iconWrap}>
          <FiMessageSquare size={14} style={{ ...iconStyle, top: 14, transform: "none" }} />
          <textarea
            name="enquiry"
            maxLength={800}
            placeholder={t("Tell us what you're looking for...")}
            value={formData.enquiry}
            onChange={handleChange}
            onFocus={() => setFocusedField("enquiry")}
            onBlur={() => setFocusedField(null)}
            style={{
              ...inputBase,
              paddingTop: 10,
              height: 100,
              resize: "vertical",
              borderColor: getBorderColor("enquiry"),
              background: getBg("enquiry"),
            }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          {errors.enquiry
            ? <span style={{ fontSize: 11, color: "#e53935" }}>{errors.enquiry}</span>
            : <span />}
          <span style={{ fontSize: 11, color: "#aaa" }}>{formData.enquiry.length} / 800</span>
        </div>
      </div>

      {/* reCAPTCHA */}
      <div style={{ marginBottom: 14 }}>
        <ReCAPTCHA ref={recaptcha} sitekey={REACT_APP_SITE_KEY} />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        style={{
          width: "100%",
          background: loading ? "#b05070" : "#7a0024",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "12px 16px",
          fontSize: 14,
          fontWeight: 700,
          cursor: loading ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          letterSpacing: "0.3px",
          transition: "background 0.2s",
        }}
      >
        <FiSend size={15} />
        {loading ? t("Submitting...") : t("Submit Enquiry")}
      </button>
    </form>
  );
};

export default withTranslation()(ContactSupportForm);